/* eslint-disable @typescript-eslint/no-explicit-any */
import { Instant, LocalDate, LocalDateTime, ZoneId } from 'js-joda';
import { oc } from 'ts-optchain';
import _ from 'lodash';

import { NoteTypeAndScope, dashboard } from 'types';
import { capitalizeName } from '@tock/shared/utils/patron';
import { core, safeEnumCompare, safeToNumber } from '@tock/coretypes';
import { isEmail } from '@tock/shared/utils/FormValidators';
import { urlIsValidAndOnDomain } from 'utils/urlUtils';
import protos from 'utils/protos';

const { GuestProfile, NoteScope, PartyState, GuestProfileGroup } = protos;

export interface GuestProfileFields {
  address_1: string;
  address_2: string;
  anniversaryDay: string;
  anniversaryMonth: string;
  anniversaryYear: string;
  birthdayDay: string;
  birthdayMonth: string;
  birthdayYear: string;
  city: string;
  company: string;
  country: string;
  facebook: string;
  image: string;
  instagram: string;
  jobTitle: string;
  linkedin: string;
  patron: {
    email: string;
    firstName: string;
    lastName: string;
    phoneCountryCode: string;
    phone: string;
    zipCode: string;
  };
  phone: {
    type: string;
    phoneCountryCode: string;
    phone: string;
  }[];
  spouse?: core.IPatron;
  spouseName: string;
  state: string;
  twitter: string;
  zipCode: string;
}

export interface AllProfilePhones {
  primaryPhone: {
    phone?: string;
    countryCode?: string;
  };
  additional: dashboard.IPhone[];
  count: number;
}

export interface PatronStatsProps {
  allBookings?: number;
  allCancellations?: number;
  allNoShows?: number;
  allWalkIns?: number;
  bookByEmail?: number;
  waitlist?: number;
}

export const DEFAULT_GENERAL_NOTE: dashboard.IAuditedNote = { note: '', noteType: 'GENERAL' };
export const DEFAULT_DIETARY_NOTE: dashboard.IAuditedNote = { note: '', noteType: 'DIETARY' };

export function getSortedBusinessGuestProfiles(guestProfile?: dashboard.GuestProfile) {
  return _.orderBy(
    oc(guestProfile).businessGuestProfile([]),
    (businessGuestProfile) => {
      return oc(businessGuestProfile).business.name();
    },
    ['asc']
  ) as dashboard.BusinessGuestProfile[];
}

export function pullCurrentProfileOffBusinessGuestProfile<
  T extends { businessGuestProfile?: dashboard.IBusinessGuestProfile[] | undefined | null },
>(
  guestProfile: T,
  currentBusinessId?: number
): T & { currentBusinessGuestProfile?: dashboard.IBusinessGuestProfile } {
  const profile: T & { currentBusinessGuestProfile?: dashboard.IBusinessGuestProfile } = _.assign(
    {},
    guestProfile
  );
  const originalBusinessGuestProfile = oc(profile).businessGuestProfile([]);
  const currentBusinessGuestProfile = _.find(
    originalBusinessGuestProfile,
    (businessGuestProfile) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return safeToNumber(businessGuestProfile.business!.id) === safeToNumber(currentBusinessId);
    }
  );
  if (currentBusinessGuestProfile) {
    profile.currentBusinessGuestProfile = currentBusinessGuestProfile;
    profile.businessGuestProfile = _.filter(
      originalBusinessGuestProfile,
      (businessGuestProfile) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return safeToNumber(businessGuestProfile.business!.id) !== safeToNumber(currentBusinessId);
      }
    );
  }
  return profile;
}

export function getRelevantAuditedNotesFromProfile(
  noteScopeAsString: string,
  profile?: dashboard.GuestProfile | dashboard.IGuestProfile
): dashboard.IAuditedNote[] {
  if (safeEnumCompare(noteScopeAsString, NoteScope, NoteScope.BUSINESS_GROUP)) {
    return oc(profile).businessGroupGuestProfile.note([]);
  } else {
    return (oc(profile) as any).currentBusinessGuestProfile.note([]);
  }
}

export function getBusinessGuestProfile(
  profile: dashboard.GuestProfile,
  businessId: number,
  scope?: string
): dashboard.IBusinessGroupGuestProfile | dashboard.IBusinessGuestProfile | null | undefined {
  if (safeEnumCompare(scope, NoteScope, NoteScope.BUSINESS_GROUP)) {
    return profile.businessGroupGuestProfile;
  } else {
    const profileToUse = profile.businessGuestProfile;
    return _.find(profileToUse, (businessProfile) => {
      if (businessProfile && businessProfile.business) {
        return safeToNumber(businessProfile.business.id) === safeToNumber(businessId);
      }
      return false;
    });
  }
}

export function getBusinessNotesForProfile(
  activeScopes: any,
  businessId: number,
  profile: dashboard.GuestProfile
) {
  return _.reduce(
    activeScopes,
    (notes: dashboard.AuditedNote[], activeScope) => {
      // first get the right Profile notes
      const businessGuestProfile = getBusinessGuestProfile(profile, businessId, activeScope.scope);
      if (businessGuestProfile && businessGuestProfile.note) {
        const noteForType: any = _.find(
          businessGuestProfile.note,
          (note) => note.noteType === activeScope.type
        );
        if (noteForType) {
          notes.push(noteForType);
          return notes;
        }
      }
      return notes;
    },
    []
  );
}

export function getGuestNoteForProfile(
  profile: dashboard.GuestProfile,
  patronNoteScopes: NoteTypeAndScope[],
  activeBusinessId: number | undefined
) {
  let notes;
  if (profile && activeBusinessId) {
    notes = getBusinessNotesForProfile(patronNoteScopes, activeBusinessId, profile);
  }

  const guestNote = _.find(
    notes as dashboard.AuditedNote[],
    (auditedNote: dashboard.AuditedNote) => {
      return auditedNote.noteType === 'GENERAL';
    }
  );

  const guestDietaryNote = _.find(
    notes as dashboard.AuditedNote[],
    (auditedNote: dashboard.AuditedNote) => {
      return auditedNote.noteType === 'DIETARY';
    }
  );

  return {
    guestNote: guestNote ?? DEFAULT_GENERAL_NOTE,
    guestDietaryNote: guestDietaryNote ?? DEFAULT_DIETARY_NOTE,
  };
}

export function getBirthday(guestProfile: dashboard.GuestProfile) {
  if (!oc(guestProfile).day()) {
    return null;
  }

  return _.find(guestProfile.day, (day) =>
    safeEnumCompare(day.type, GuestProfile.DayType, GuestProfile.DayType.BIRTHDAY)
  );
}

export function findLinkOfType(
  linkType: number,
  listOfLinks?: dashboard.ILink[]
): string | undefined {
  const link = _.find(listOfLinks, (link) => {
    return safeEnumCompare(link.type, GuestProfile.LinkType, linkType);
  });
  return oc(link).link() || undefined;
}

export function getInitialValuesFromProfileProto(
  profile: dashboard.GuestProfile
): GuestProfileFields {
  const anniversary = _.find(profile.day, (day) => {
    return safeEnumCompare(day.type, GuestProfile.DayType, GuestProfile.DayType.ANNIVERSARY);
  });
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const birthday = _.find(profile.day, (day) => {
    return safeEnumCompare(day.type, GuestProfile.DayType, GuestProfile.DayType.BIRTHDAY);
  })!;
  const imageLink = findLinkOfType(GuestProfile.LinkType.IMAGE, profile.link);

  const importedGuestData = oc(profile).importedProfile.link([]);
  const facebookLink =
    findLinkOfType(GuestProfile.LinkType.FACEBOOK, profile.link) ||
    findLinkOfType(GuestProfile.LinkType.FACEBOOK, importedGuestData);
  const twitterLink =
    findLinkOfType(GuestProfile.LinkType.TWITTER, profile.link) ||
    findLinkOfType(GuestProfile.LinkType.TWITTER, importedGuestData);
  const linkedInLink =
    findLinkOfType(GuestProfile.LinkType.LINKEDIN, profile.link) ||
    findLinkOfType(GuestProfile.LinkType.LINKEDIN, importedGuestData);
  const instagramLink =
    findLinkOfType(GuestProfile.LinkType.INSTAGRAM, profile.link) ||
    findLinkOfType(GuestProfile.LinkType.INSTAGRAM, importedGuestData);

  const anniversaryYear = _.toString(oc(anniversary).year());
  const anniversaryDay = _.toString(oc(anniversary).day());
  const birthdayYear = _.toString(oc(birthday).year());
  const birthdayDay = _.toString(oc(birthday).day());

  return {
    patron: {
      firstName: oc(profile).patron.firstName(''),
      lastName: oc(profile).patron.lastName(''),
      email: oc(profile).patron.email(''),
      phone: oc(profile).patron.phone(''),
      phoneCountryCode: oc(profile).patron.phoneCountryCode(''),
      zipCode: oc(profile).patron.zipCode(''),
    },
    image: imageLink || '',

    address_1: oc(profile).address.address_1(''),

    address_2: oc(profile).address.address_2(''),
    city: oc(profile).address.city(''),
    state: oc(profile).address.state(''),
    country: oc(profile).address.country(''),
    zipCode: oc(profile).address.zipCode(''),
    birthdayDay: birthdayDay ? birthdayDay : '',
    birthdayMonth: _.toString(oc(birthday).month(0)), // 0 corresponds to no choice in our month drop down picker
    birthdayYear: birthdayYear ? birthdayYear : '',
    anniversaryDay: anniversaryDay ? anniversaryDay : '',
    anniversaryMonth: _.toString(oc(anniversary).month(0)), // 0 corresponds to no choice in our month drop down picker
    anniversaryYear: anniversaryYear ? anniversaryYear : '',
    company: profile.company || '',
    jobTitle: profile.jobTitle || '',
    facebook: facebookLink || '',
    twitter: twitterLink || '',
    linkedin: linkedInLink || '',
    instagram: instagramLink || '',
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    spouse: profile.spouse!,
    spouseName: profile.spouseName || '',
    phone: _.map(oc(profile).phone(), (phone: any) => ({
      type: phone.type,
      phoneCountryCode: phone.phoneCountryCode,
      phone: phone.phone || '',
    })),
  };
}

export type GuestProfileFormData = GuestProfileFields;

export const guestProfileFields = [
  'patron.firstName',
  'patron.lastName',
  'patron.email',
  'patron.phoneCountryCode',
  'patron.phone',
  'patron.zipCode',
  'anniversaryDay',
  'anniversaryMonth',
  'anniversaryYear',
  'birthdayDay',
  'birthdayMonth',
  'birthdayYear',
  'address_1',
  'address_2',
  'city',
  'state',
  'country',
  'zipCode',
  'phone[].type',
  'phone[].phoneCountryCode',
  'phone[].phone',
  'image',
  'company',
  'jobTitle',
  'instagram',
  'facebook',
  'twitter',
  'linkedin',
  'spouse',
  'spouseName',
];

export const validate = (values: any, props: any) => {
  const errors: any = {};
  if (values.facebook && !urlIsValidAndOnDomain(values.facebook, 'facebook.com')) {
    errors.facebook = 'Please enter a valid facebook.com url.';
  }

  if (values.twitter && !urlIsValidAndOnDomain(values.twitter, 'twitter.com')) {
    errors.twitter = 'Please enter a valid twitter.com url.';
  }
  if (values.linkedin && !urlIsValidAndOnDomain(values.linkedin, 'linkedin.com')) {
    errors.linkedin = 'Please enter a valid linkedin.com url.';
  }
  if (values.instagram && !urlIsValidAndOnDomain(values.instagram, 'instagram.com')) {
    errors.instagram = 'Please enter a valid instagram.com url.';
  }

  errors.patron = {};
  if (
    props.initialValues.patron.firstName !== values.patron.firstName &&
    !values.patron.firstName
  ) {
    errors.patron.firstName = 'Enter a first name';
  }
  if (props.initialValues.patron.lastName !== values.patron.lastName && !values.patron.lastName) {
    errors.patron.lastName = 'Enter a last name';
  }
  if (props.initialValues.patron.email !== values.patron.email && !isEmail(values.patron.email)) {
    errors.patron.email = 'Enter a valid email';
  }

  const {
    birthdayDay,
    birthdayMonth,
    birthdayYear,
    anniversaryDay,
    anniversaryMonth,
    anniversaryYear,
  } = values;

  // Validate birthday fields
  const birthdayErrors = validateDateFields(birthdayDay, birthdayMonth, birthdayYear, 'birthday');
  Object.assign(errors, birthdayErrors);

  // Validate anniversary fields
  const anniversaryErrors = validateDateFields(
    anniversaryDay,
    anniversaryMonth,
    anniversaryYear,
    'anniversary'
  );
  Object.assign(errors, anniversaryErrors);

  return errors;
};

/**
 * Validates date fields (day, month, year) for a given date type (birthday or anniversary).
 * Returns an object with error messages for any invalid fields.
 */
function validateDateFields(
  day: string | undefined,
  month: string | undefined,
  year: string | undefined,
  fieldPrefix: string
): { [key: string]: string } {
  const errors: { [key: string]: string } = {};
  const dayField = `${fieldPrefix}Day`;
  const monthField = `${fieldPrefix}Month`;
  const yearField = `${fieldPrefix}Year`;

  // Check if month and day are both present or both absent
  if (month && _.toNumber(month) > 0 && !day) {
    errors[dayField] = 'Enter a day';
  }
  if (day && (!month || _.toNumber(month) === 0)) {
    errors[monthField] = 'Select a month';
  }

  // Validate day
  if (day) {
    const dayNum = _.toNumber(day);
    if (!_.isFinite(dayNum) || dayNum < 1 || dayNum > 31) {
      errors[dayField] = 'Enter a valid day';
    }
  }

  // Validate year
  if (year) {
    const yearNum = _.toNumber(year);
    if (!_.isFinite(yearNum) || _.toString(year).length !== 4) {
      errors[yearField] = 'Enter a valid year';
    }
  }

  // Validate complete date is valid for the given month
  if (month && day && !errors[dayField] && !errors[monthField]) {
    const monthNum = _.toNumber(month);
    const dayNum = _.toNumber(day);

    if (monthNum >= 1 && monthNum <= 12) {
      if (year && !errors[yearField]) {
        // If we have a year, use LocalDate.of to validate
        const yearNum = _.toNumber(year);
        try {
          LocalDate.of(yearNum, monthNum, dayNum);
        } catch (_e) {
          errors[dayField] = 'Invalid date for the selected month';
        }
      } else {
        // If we don't have a year, manually check days per month
        // Allow Feb 29 since we don't know if it's a leap year
        const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
        if (dayNum > daysInMonth[monthNum - 1]) {
          errors[dayField] = 'Invalid date for the selected month';
        }
      }
    }
  }

  return errors;
}

function getValidMonth(month: string): number | null {
  const monthNumber = _.toNumber(month);
  if (_.isFinite(monthNumber) && monthNumber >= 1 && monthNumber <= 12) {
    return monthNumber;
  }
  return null;
}

function getValidDay(day: string): number | null {
  const dayNumber = _.toNumber(day);
  if (_.isFinite(dayNumber) && dayNumber >= 1 && dayNumber <= 31) {
    return dayNumber;
  }
  return null;
}

function getValidYear(year: string): number | null {
  const yearNum = _.toNumber(year);
  if (_.isFinite(yearNum) && year.length === 4) {
    return yearNum;
  }
  return null;
}

export function buildGuestProfileProtoFromForm(
  newValues: any,
  patron: core.IPatron
): dashboard.GuestProfile {
  const guestProfile: dashboard.IGuestProfile = {};
  const addressFields = _.mapValues(
    _.pick(newValues, ['address_1', 'address_2', 'city', 'country', 'state', 'zipCode']),
    _.trim
  );
  if (!_.isEmpty(_.values(addressFields))) {
    guestProfile.address = addressFields;
  }
  guestProfile.jobTitle = newValues.jobTitle ? _.trim(newValues.jobTitle) : null;
  guestProfile.company = newValues.company ? _.trim(newValues.company) : null;
  guestProfile.spouse = newValues.spouse || null;
  guestProfile.spouseName = newValues.spouseName ? _.trim(newValues.spouseName) : null;
  guestProfile.patron = {
    ...patron,
    ...newValues.patron,
  };
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  guestProfile.patron!.firstName = capitalizeName(guestProfile.patron!.firstName!);
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  guestProfile.patron!.lastName = capitalizeName(guestProfile.patron!.lastName!);
  guestProfile.day = [];
  guestProfile.link = [];
  guestProfile.phone =
    _.filter(newValues.phone as dashboard.IPhone[], (phone) => !!oc(phone).phone()) || [];

  if (newValues.birthdayDay || newValues.birthdayMonth || newValues.birthdayYear) {
    const day = {
      type: 'BIRTHDAY' as dashboard.GuestProfile.DayType,
      day: getValidDay(newValues.birthdayDay),
      month: getValidMonth(newValues.birthdayMonth),
      year: getValidYear(newValues.birthdayYear),
    };
    guestProfile.day.push(day);
  }

  if (newValues.anniversaryDay || newValues.anniversaryMonth || newValues.anniversaryYear) {
    const day = {
      type: 'ANNIVERSARY' as dashboard.GuestProfile.DayType,
      day: getValidDay(newValues.anniversaryDay),
      month: getValidMonth(newValues.anniversaryMonth),
      year: getValidYear(newValues.anniversaryYear),
    };
    guestProfile.day.push(day);
  }
  if (_.trim(newValues.facebook)) {
    guestProfile.link.push({ type: 'FACEBOOK', link: _.trim(newValues.facebook) });
  }

  if (_.trim(newValues.twitter)) {
    guestProfile.link.push({ type: 'TWITTER', link: _.trim(newValues.twitter) });
  }

  if (_.trim(newValues.linkedin)) {
    guestProfile.link.push({ type: 'LINKEDIN', link: _.trim(newValues.linkedin) });
  }

  if (_.trim(newValues.instagram)) {
    guestProfile.link.push({ type: 'INSTAGRAM', link: _.trim(newValues.instagram) });
  }

  if (newValues.image && !newValues.image.startsWith('blob:')) {
    // Don't include the existing image if a new image is available for uploading.
    guestProfile.link.push({ type: 'IMAGE', link: _.trim(newValues.image) });
  }

  return guestProfile as dashboard.GuestProfile;
}

/*
 * Handles the update of the guest profile form.
 * Checks for changed notes first and submits them separately a
 * Checks if any of the profile fields have changed and if so, submits it.
 */
export function updateProfile(
  actions: {
    guestProfile: {
      update: (request: dashboard.IGuestProfile, toast?: string) => Promise<any>;
      updateImage: (patronId: number, file: any) => Promise<any>;
    };
  },
  patron: core.Patron,
  newImageFile: File | null,
  profileForm: any
) {
  const guestProfileActions = actions.guestProfile;
  const maybeChangedProfile = buildGuestProfileProtoFromForm(profileForm, patron);

  return guestProfileActions.update(maybeChangedProfile).then((response: ProtoResponse<any>) => {
    if (oc(patron).id()) {
      window.gaTrack('send', 'event', 'Guest directory', 'Guest modified');
    } else {
      window.gaTrack('send', 'event', 'Guest directory', 'Guest added');
    }
    if (newImageFile) {
      return guestProfileActions
        .updateImage(safeToNumber(patron.id), newImageFile)
        .then(() => response);
    }
    return response;
  });
}

export function buildWalkInProfile(
  firstName: string,
  lastName: string,
  phone: string,
  phoneCountryCode: string,
  email: string
): dashboard.IGuestProfile {
  return {
    patron: {
      firstName,
      lastName,
      email,
      phone,
      phoneCountryCode,
    },
    /* Phone is intentionally omitted here. Since the business is allowed to
     * set the patron's phone directly, including it here would duplicate it. */
    phone: [],
  };
}

export function getRecentVisits(
  businessId: number[],
  timeZone: string,
  patronId?: number,
  guestPurchaseHistory?: core.PastPurchase[],
  guestWalkInHistory?: dashboard.VisitSummary[],
  isUpcoming?: boolean,
  limit = 3
) {
  const purchases = _.filter(guestPurchaseHistory, (visitHistory) => {
    const date = LocalDateTime.parse(visitHistory.ticketDateTime).toLocalDate();
    const today = LocalDate.now(ZoneId.of(timeZone));
    return (
      _.includes(businessId, safeToNumber(oc(visitHistory.business).id())) &&
      visitHistory.latestPurchase &&
      !visitHistory.cancelledOrRefunded &&
      visitHistory.primaryState !== PartyState.NO_SHOW &&
      (isUpcoming ? today.isBefore(date) : today.isAfter(date)) &&
      oc(visitHistory).dinerPatron[0].id() === patronId
    );
  });

  const walkIns = !isUpcoming
    ? _.filter(
        guestWalkInHistory,
        (walkIn) =>
          _.has(walkIn, 'seatedTimeSecs') &&
          _.includes(businessId, safeToNumber(oc(walkIn.business).id()))
      )
    : [];

  const bookedAndWalkInDates = _.flatten(
    _.concat(
      _.map(purchases, (purchase: core.PastPurchase) => {
        return {
          purchaseId: oc(purchase).id(null as any),
          date: oc(purchase).visit.serviceDate(
            LocalDateTime.parse(purchase.ticketDateTime).toLocalDate().toString()
          ),
          visitId: safeToNumber(oc(purchase).visit.id(null)),
          hasPurchase: true,
        };
      }),
      _.map(walkIns, (walkIn) => {
        return {
          purchaseId: null as any,
          date: LocalDateTime.ofInstant(
            Instant.ofEpochSecond(walkIn.seatedTimeSecs),
            ZoneId.of(timeZone)
          )
            .toLocalDate()
            .toString(),
          visitId: walkIn.visitId,
          hasPurchase: false,
        };
      })
    )
  );

  const mostRecent = _.orderBy(bookedAndWalkInDates, 'date');
  if (!isUpcoming) {
    return _.reverse(_.takeRight(mostRecent, limit));
  } else {
    return _.reverse(_.take(mostRecent, limit));
  }
}

// additional phone numbers are on the profile, and the primary is on the patron
export function getAllPhones(profile: dashboard.GuestProfile): AllProfilePhones {
  const profilePhone = oc(profile).phone([]);
  const patronPhone = oc(profile).patron.phone();
  const patronPhoneCountryCode = oc(profile).patron.phoneCountryCode();
  return {
    primaryPhone: {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      phone: patronPhone!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      countryCode: patronPhoneCountryCode!,
    },
    additional: profilePhone,
    count: profilePhone.length + (patronPhone ? 1 : 0),
  };
}

export function getGuestProfileStats(
  profile?: dashboard.BusinessGuestProfile | dashboard.IBusinessGuestProfile,
  patronStats?: {
    businessName: string;
    stats: PatronStatsProps;
  }
) {
  return {
    upcomingVisits: profile?.upcomingVisits ?? 0,
    pastVisits: profile?.pastVisits ?? 0,
    cancellations: profile?.cancellations ?? 0,
    noShows: patronStats?.stats.allNoShows ?? 0,
    requestedVisits: (patronStats?.stats?.waitlist ?? 0) + (patronStats?.stats?.bookByEmail ?? 0),
  };
}

export function groupStringSet(
  stringDimension: dashboard.GuestProfileGroup.GuestProfileDimensions,
  values: string[]
): dashboard.StringSet {
  return {
    field: GuestProfileGroup.GuestProfileDimensions[stringDimension],
    value: values,
  };
}

export function groupValueSet(
  valueDimension: dashboard.GuestProfileGroup.GuestProfileDimensions,
  values: number[]
): dashboard.ValueSet {
  return {
    field: GuestProfileGroup.GuestProfileDimensions[valueDimension],
    value: values,
  };
}

export function groupMatchSet(
  valueDimension: dashboard.GuestProfileGroup.GuestProfileDimensions,
  value: string,
  matchType: dashboard.MatchSet.MatchType
): dashboard.MatchSet {
  return {
    field: GuestProfileGroup.GuestProfileDimensions[valueDimension],
    value,
    matchType,
  };
}

/**
 * Determines if the given guest profile contains information that should be
 * displayed as "Tock profile" information in CRM.
 */
export function hasRelevantTockProfileInfo(profile: dashboard.IGuestProfile): boolean {
  const {
    patronProfileAversions,
    patronProfileDietaryRestriction,
    patronProfileHospitalityPreference,
    patronProfileShortBio,
  } = profile;
  return !!(
    patronProfileAversions ||
    (patronProfileDietaryRestriction && patronProfileDietaryRestriction.length > 0) ||
    (patronProfileHospitalityPreference && patronProfileHospitalityPreference.length > 0) ||
    patronProfileShortBio
  );
}
